import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from './ObfuscatedPhone';
import { GoogleBusinessFooter } from './GoogleBusiness';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_construction-hub-125/artifacts/hiq4o5va_Logo%20001%20-%20TR%20Kenscho%20Design.png";

export const Footer = () => {
  const { t, getLocalizedLink } = useLanguage();

  return (
    <footer className="bg-foreground text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div>
            <img 
              src={LOGO_URL} 
              alt="Kenscho Design" 
              className="h-16 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-white/70 leading-relaxed">
              {t('footer.desc')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedLink('/')} className="text-white/70 hover:text-white transition-colors">{t('nav.home')}</Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/services')} className="text-white/70 hover:text-white transition-colors">{t('nav.construction')}</Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/equipment-rental')} className="text-white/70 hover:text-white transition-colors">{t('nav.equipmentRental')}</Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/contact')} className="text-white/70 hover:text-white transition-colors">{t('nav.contact')}</Link>
              </li>
            </ul>
          </div>

          {/* Divisions */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('footer.ourDivisions')}</h4>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedLink('/work-site')} className="text-white/70 hover:text-white transition-colors">
                  Kenscho {t('nav.workSite')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/transport')} className="text-white/70 hover:text-white transition-colors">
                  Kenscho {t('nav.transport')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/concrete')} className="text-white/70 hover:text-white transition-colors">
                  Kenscho {t('nav.concrete')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/excavation')} className="text-white/70 hover:text-white transition-colors">
                  Kenscho {t('nav.excavation')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedLink('/welding')} className="text-white/70 hover:text-white transition-colors">
                  Kenscho {t('nav.welding')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-white/70">
                  Lanaudière, Laurentians, Laval<br />& Montreal, QC
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-primary shrink-0" />
                <a href="mailto:info@kenscho.ca" className="text-white/70 hover:text-white transition-colors">
                  info@kenscho.ca
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-primary shrink-0" />
                <ObfuscatedPhone className="text-white/70 hover:text-white" />
              </li>
            </ul>

            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              <a 
                href="https://instagram.com/kenschodesign" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-white/10 rounded-full hover:bg-primary transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="#" 
                className="p-2 bg-white/10 rounded-full hover:bg-primary transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="#" 
                className="p-2 bg-white/10 rounded-full hover:bg-primary transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Google Business */}
          <GoogleBusinessFooter />
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} Kenscho Design. {t('footer.rights')}
          </p>
          <p className="text-white/50 text-sm">
            www.kenscho.ca
          </p>
        </div>
      </div>
    </footer>
  );
};
